import { FeedbackOnFeedbackActionType } from '@modules/feedback/store/feedbackOnFeedbackActionType';
import { feedbackOnFeedbackInitialState } from '@modules/feedback/store/feedbackOnFeedbackInitialState';
import {
  FeedbackOnFeedbackAction,
  FeedbackOnFeedbackState,
} from '@modules/feedback/store/types';
import { getFilteredEditedRecord } from '../utils';
import { FeedbackLabelType } from 'v2/core/entities/feedback';

export function feedbackOnFeedbackReducer(
  state: FeedbackOnFeedbackState,
  action: FeedbackOnFeedbackAction,
): FeedbackOnFeedbackState {
  switch (action.type) {
    case FeedbackOnFeedbackActionType.SET_RECORD_IN_EDIT_MODE: {
      return {
        ...state,
        recordInEditMode: action.payload
          ? {
              ...state.recordInEditMode,
              ...action.payload,
            }
          : action.payload,
      };
    }

    case FeedbackOnFeedbackActionType.SET_EDITED_RECORDS: {
      return {
        ...state,
        editedRecords: getFilteredEditedRecord(action.payload),
      };
    }

    case FeedbackOnFeedbackActionType.RESET_RECORD_IN_EDIT_MODE: {
      return {
        ...state,
        recordInEditMode: feedbackOnFeedbackInitialState.recordInEditMode,
      };
    }

    case FeedbackOnFeedbackActionType.SET_SHOULD_REFETCH: {
      return {
        ...state,
        shouldRefetch: action.payload,
      };
    }

    case FeedbackOnFeedbackActionType.SET_HAS_PREDICTION_FADED_IN: {
      const { feedbackID, labelID, hasFadedIn } = action.payload;

      const updatedEditedRecords = state.editedRecords.map((record) => {
        if (record.id !== feedbackID) {
          return record;
        }

        const updatedEditPredictions =
          record.editPredictions?.map((editPrediction) => {
            const isTargetPrediction =
              (editPrediction.type === FeedbackLabelType.Reason ||
                editPrediction.type === FeedbackLabelType.Entity) &&
              editPrediction.prediction.id === labelID;

            if (!isTargetPrediction) {
              return editPrediction;
            }

            return { ...editPrediction, hasFadedIn };
          }) ?? [];

        return { ...record, editPredictions: updatedEditPredictions };
      });

      return { ...state, editedRecords: updatedEditedRecords };
    }

    case FeedbackOnFeedbackActionType.SET_FLAG_ON_RECORD_SUMMARY: {
      const { id, keywordSummaryHash, summaryFlagType } = action.payload;

      const clonedFlaggedSummaryRecords = [...state.flaggedSummaryRecords];

      const index = clonedFlaggedSummaryRecords.findIndex(
        (record) =>
          record.id === id && record.keywordSummaryHash === keywordSummaryHash,
      );

      if (index > -1) {
        clonedFlaggedSummaryRecords[index] = {
          ...clonedFlaggedSummaryRecords[index],
          summaryFlagType,
        };
      } else {
        clonedFlaggedSummaryRecords.push({
          id,
          keywordSummaryHash,
          summaryFlagType,
        });
      }

      return {
        ...state,
        flaggedSummaryRecords: clonedFlaggedSummaryRecords,
      };
    }

    case FeedbackOnFeedbackActionType.REMOVE_FLAG_FROM_RECORD_SUMMARY: {
      const { id, keywordSummaryHash } = action.payload;

      const clonedFlaggedSummaryRecords = [...state.flaggedSummaryRecords];

      const index = clonedFlaggedSummaryRecords.findIndex(
        (record) =>
          record.id === id && record.keywordSummaryHash === keywordSummaryHash,
      );

      if (index > -1) {
        clonedFlaggedSummaryRecords[index] = {
          ...clonedFlaggedSummaryRecords[index],
          summaryFlagType: undefined,
        };
      } else {
        clonedFlaggedSummaryRecords.push({
          id,
          keywordSummaryHash,
          summaryFlagType: undefined,
        });
      }

      return {
        ...state,
        flaggedSummaryRecords: clonedFlaggedSummaryRecords,
      };
    }

    default:
      return state;
  }
}
